import { normalizeStatus } from "./identityVerificationStatus";

export const STATUS_POLL_INTERVAL_MS = 5000;
export const DECISION_RECONCILIATION_DELAY_MS = 5 * 60 * 1000;
export const DELAYED_STATUS_POLL_INTERVAL_MS = 30 * 1000;
export const MAX_STATUS_REFRESH_FAILURES = 5;
export const MAX_STATUS_REFRESH_BACKOFF_MS = 60 * 1000;

export const statusRefreshDelay = (consecutiveFailures) => {
  if (!consecutiveFailures) return STATUS_POLL_INTERVAL_MS;
  return Math.min(
    STATUS_POLL_INTERVAL_MS * (2 ** Math.max(0, consecutiveFailures - 1)),
    MAX_STATUS_REFRESH_BACKOFF_MS,
  );
};

const RECONCILED_STATUSES = new Set([
  "verified",
  "approved",
  "declined",
  "failed",
  "expired",
  "cancelled",
]);

export const decisionReconciliationStorageKey = (guestReservationId) =>
  `identity-verification-decision-reconciliation:${guestReservationId}`;

export const readDecisionReconciliation = (guestReservationId) => {
  if (!guestReservationId || typeof window === "undefined") return null;
  const key = decisionReconciliationStorageKey(guestReservationId);
  try {
    const value = JSON.parse(window.sessionStorage.getItem(key));
    const startedAt = Number(value?.startedAt);
    if (!value?.baselineStatus || !Number.isFinite(startedAt)) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return {
      baselineStatus: normalizeStatus(value.baselineStatus),
      startedAt,
    };
  } catch (_error) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_storageError) {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
    return null;
  }
};

export const getDecisionReconciliationStatus = (
  reconciliation,
  now = Date.now(),
) =>
  now >= reconciliation.startedAt + DECISION_RECONCILIATION_DELAY_MS
    ? "sync_delayed"
    : "syncing";

export const isDecisionReconciliationResolved = (reconciliation, status) => {
  const normalizedStatus = normalizeStatus(status);
  return (
    normalizedStatus !== reconciliation.baselineStatus &&
    RECONCILED_STATUSES.has(normalizedStatus)
  );
};

export const writeDecisionReconciliation = (
  guestReservationId,
  reconciliation,
) => {
  if (!guestReservationId || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      decisionReconciliationStorageKey(guestReservationId),
      JSON.stringify(reconciliation),
    );
  } catch (_error) {
    // Reconciliation still runs in memory when storage is unavailable.
  }
};

export const clearDecisionReconciliation = (guestReservationId) => {
  if (!guestReservationId || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(
      decisionReconciliationStorageKey(guestReservationId),
    );
  } catch (_error) {
    // An unavailable storage backend needs no further cleanup.
  }
};
